import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 * @property {HTMLButtonElement} [toggleButton] - The pause/play toggle button (WCAG 2.2.2).
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    const { videoSources, videoElement, toggleButton } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();

    if (toggleButton) {
      this.#syncToggleState();
      videoElement.addEventListener('play', () => this.#syncToggleState());
      videoElement.addEventListener('pause', () => this.#syncToggleState());
    }
  }

  togglePlayback() {
    const { videoElement } = this.refs;
    if (videoElement.paused) {
      videoElement.play();
    } else {
      videoElement.pause();
    }
  }

  #syncToggleState() {
    const { videoElement, toggleButton } = this.refs;
    if (!toggleButton) return;

    const isPaused = videoElement.paused;
    toggleButton.dataset.state = isPaused ? 'paused' : 'playing';
    const label = isPaused ? toggleButton.dataset.labelPlay : toggleButton.dataset.labelPause;
    if (label) toggleButton.setAttribute('aria-label', label);
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
